/**
 * Backend origin for all `API_ENDPOINTS` (blogs, poojas, auth, PATCH /read, etc.).
 * Prefer `REACT_APP_API_BASE_URL_DEV` / `REACT_APP_API_BASE_URL_PROD` via `REACT_APP_ENV`
 * (`npm run dev` → DEV, `npm run prod` / `npm run build` → PROD).
 * Legacy: `REACT_APP_API_BASE_URL`. Empty value → relative `/api` via `src/setupProxy.js`.
 */
function resolveApiBaseUrl() {
  const mode = String(process.env.REACT_APP_ENV || '').trim().toLowerCase();
  const devUrl = String(process.env.REACT_APP_API_BASE_URL_DEV || '').trim();
  const prodUrl = String(process.env.REACT_APP_API_BASE_URL_PROD || '').trim();
  const legacy = String(process.env.REACT_APP_API_BASE_URL || '').trim();

  // Legacy override always wins when set — that's the point of "uncomment to force".
  if (legacy) return legacy.replace(/\/$/, '');

  let chosen;
  if (mode === 'prod' || mode === 'production') chosen = prodUrl;
  else if (mode === 'dev' || mode === 'development') chosen = devUrl;
  else if (process.env.NODE_ENV === 'production') chosen = prodUrl;
  else chosen = devUrl;

  if (!chosen) {
    if (mode === 'prod' || mode === 'production' || process.env.NODE_ENV === 'production') {
      return 'https://vectors.daivbharathi.com';
    }
    return 'https://vectors.daiv.co.in';
  }
  return String(chosen).replace(/\/$/, '');
}

export const API_BASE_URL = resolveApiBaseUrl();

/**
 * GET /api/auth/me — on localhost dev server use relative URL so setupProxy forwards to REACT_APP_PROXY_TARGET.
 */
export function getAuthMeEndpoint() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return '/api/auth/me';
    }
  }
  return `${API_BASE_URL}/api/auth/me`;
}

/** Resolve image URL – prepend API_BASE_URL for relative paths */
export const resolveImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const base = API_BASE_URL.replace(/\/$/, '');
  if (trimmed.startsWith('/')) return `${base}${trimmed}`;
  return `${base}/${trimmed}`;
};

/** Base for `/api/media-proxy` — same-origin in dev (setupProxy), API host in production */
export function getMediaProxyBaseUrl() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return '';
    }
  }
  return API_BASE_URL.replace(/\/$/, '');
}

/**
 * Same-origin proxy URL so canvas can read pixels (CORS-safe fetch).
 * Used when baking watermarks for Save Image / gallery downloads.
 */
export function getCanvasSafeImageUrl(url) {
  const resolved = resolveImageUrl(url);
  if (!resolved) return '';
  if (typeof window === 'undefined') return resolved;
  try {
    if (new URL(resolved).origin === window.location.origin) return resolved;
  } catch {
    return resolved;
  }
  const base = getMediaProxyBaseUrl();
  return `${base}/api/media-proxy?url=${encodeURIComponent(resolved)}`;
}

/** Base URL for share links – set REACT_APP_SITE_URL in .env for production, else uses current origin */
export const getSiteUrl = () =>
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://daiv.co');

/** Support line — same as Contact page; WhatsApp uses digits only for wa.me */
export const SUPPORT_PHONE_TEL = 'tel:+918123858640';
export const SUPPORT_PHONE_DISPLAY = '+91 81238 58640';
export const SUPPORT_WHATSAPP_URL = 'https://wa.me/918123858640';
export const SUPPORT_EMAIL = 'social@daiv.co.in';
export const SUPPORT_EMAIL_MAILTO = 'mailto:social@daiv.co.in';

export const API_ENDPOINTS = {
  authLogin: `${API_BASE_URL}/api/auth/login`,
  authSendOtp: `${API_BASE_URL}/api/auth/send-otp`,
  authVerifyOtp: `${API_BASE_URL}/api/auth/verify-otp`,
  authVerifyMobileOtp: `${API_BASE_URL}/api/auth/verify-mobile-otp`,
  authCreateAccount: `${API_BASE_URL}/api/auth/create-account`,
  /** GET current user from JWT — used for ?tkn= WhatsApp deep links (see getAuthMeEndpoint()) */
  authMe: `${API_BASE_URL}/api/auth/me`,
  /** GET list — optional `?lang=en|hi|kn|te` for localized fields */
  poojas: `${API_BASE_URL}/api/poojas/users`,
  /** GET website list by schedule — `?type=PAST|LIVE|UPCOMING`, optional `?lang=`, `?page=`, `?size=` */
  poojasBySchedule: `${API_BASE_URL}/api/poojas/website`,
  /** GET daiv blogs list (paginated): /api/daivblogs/all?page=&size= */
  daivBlogs: (page = 0, size = 20) =>
    `${API_BASE_URL}/api/daivblogs/all?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
  /** GET single blog by id (UUID). Slug paths 404 on the API — resolve slug via list first. */
  daivBlogBySlug: (slugOrId) =>
    `${API_BASE_URL}/api/daivblogs/${encodeURIComponent(String(slugOrId || '').trim())}`,
  /** GET blogs related to a pooja: /api/daivblogs/pooja/{poojaDetailsId} */
  daivBlogsByPooja: (poojaDetailsId) => `${API_BASE_URL}/api/daivblogs/pooja/${poojaDetailsId}`,
  /** PATCH increment read count — /api/daivblogs/{blogId}/read */
  daivBlogRead: (blogId) => `${API_BASE_URL}/api/daivblogs/${blogId}/read`,
  /** GET pooja details by ID or slug: /api/poojas/{idOrSlug} */
  poojaById: (poojaDetailsId) => `${API_BASE_URL}/api/poojas/${poojaDetailsId}`,
  poojaBySlug: (slug) => `${API_BASE_URL}/api/poojas/${slug}`,
  /** GET complete pooja details by slug + schedule: /api/poojas/details?slug=...&scheduleType=LIVE&lang=en */
  poojaDetailsBySlug: ({ slug, scheduleType = 'LIVE', lang = 'en' }) =>
    `${API_BASE_URL}/api/poojas/details?${new URLSearchParams({
      slug: String(slug || ''),
      scheduleType: String(scheduleType || 'LIVE').toUpperCase(),
      lang: String(lang || 'en'),
    }).toString()}`,
  /**
   * GET poojas with a live YouTube link — `/api/poojas/live-links?lang=&type=LIVE|UPCOMING|PAST&status=`
   */
  poojaLiveLinks: ({ lang = 'en', type, status } = {}) => {
    const params = new URLSearchParams();
    if (lang) params.set('lang', String(lang));
    if (type) params.set('type', String(type).toUpperCase());
    if (status === true || status === false) params.set('status', String(status));
    else if (status != null && String(status).trim() !== '') params.set('status', String(status));
    const q = params.toString();
    return `${API_BASE_URL}/api/poojas/live-links${q ? `?${q}` : ''}`;
  },
  /** GET live chat room for a pooja — `/api/poojas/{poojaId}/chat` */
  poojaChatRoom: (poojaId) =>
    `${API_BASE_URL}/api/poojas/${encodeURIComponent(String(poojaId || ''))}/chat`,
  /**
   * Live viewer presence (public, no JWT) — keyed by poojaId from live-links.
   * POST join / heartbeat / leave; GET count (poll).
   */
  liveViewerJoin: (poojaId) =>
    `${API_BASE_URL}/api/live/${encodeURIComponent(String(poojaId || ''))}/viewer/join`,
  liveViewerHeartbeat: (poojaId) =>
    `${API_BASE_URL}/api/live/${encodeURIComponent(String(poojaId || ''))}/viewer/heartbeat`,
  liveViewerLeave: (poojaId) =>
    `${API_BASE_URL}/api/live/${encodeURIComponent(String(poojaId || ''))}/viewer/leave`,
  liveViewerCount: (poojaId) =>
    `${API_BASE_URL}/api/live/${encodeURIComponent(String(poojaId || ''))}/viewer/count`,
  /** Public live-pooja chat (LIVE_CHAT / CRM ws-chat contract). */
  chatAuthStart: `${API_BASE_URL}/api/chat/auth/start`,
  chatAuthVerify: `${API_BASE_URL}/api/chat/auth/verify`,
  chatMessages: (chatRoomId, page = 0, size = 50) =>
    `${API_BASE_URL}/api/chat/${encodeURIComponent(String(chatRoomId || ''))}/messages?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
  chatBanStatus: (chatRoomId, mobileNumber) =>
    `${API_BASE_URL}/api/chat/${encodeURIComponent(String(chatRoomId || ''))}/ban-status?${new URLSearchParams({
      mobileNumber: String(mobileNumber || ''),
    }).toString()}`,
  /** SockJS + STOMP — same host as CRM `CHAT_WEBSOCKET_URL`. */
  chatWsEndpoint: `${API_BASE_URL}/api/ws-chat`,
  poojaBookingInitiate: `${API_BASE_URL}/api/poojabookings/initiate`,
  /** POST validate coupon for pooja checkout (public; no JWT). */
  couponsPreview: `${API_BASE_URL}/api/coupons/preview`,
  /** GET add-on seva by id (public read — images may be localized). */
  sevaById: (sevaId) =>
    `${API_BASE_URL}/api/sevas/${encodeURIComponent(String(sevaId || ''))}`,
  /**
   * POST (body typically {}) — create or resume Razorpay order for an existing booking.
   * Backend should mirror initiate response shape: order_id, booking id, optional keyId.
   */
  poojaBookingCreateOrder: (bookingId) => `${API_BASE_URL}/api/poojabookings/${bookingId}/create-order`,
  /** GET list of user's bookings: /api/poojabookings?userId=xxx */
  poojaBookingsByUser: `${API_BASE_URL}/api/poojabookings`,
  /** GET single booking: /api/poojabookings/{bookingId}?userId=xxx */
  poojaBookingById: (bookingId) => `${API_BASE_URL}/api/poojabookings/${bookingId}`,
  /** PATCH booking persons: /api/poojabookings/{bookingId} */
  poojaBookingUpdate: (bookingId) => `${API_BASE_URL}/api/poojabookings/${bookingId}`,
  /** POST cancel request: /api/poojabookings/{bookingId}/cancel-request */
  poojaBookingCancelRequest: (bookingId) => `${API_BASE_URL}/api/poojabookings/${bookingId}/cancel-request`,
  /** GET active event banners (requires token) */
  eventBannersActive: `${API_BASE_URL}/api/event-banners/active`,
  /** POST contact form: /api/contact-us */
  contactUs: `${API_BASE_URL}/api/contact-us`,
  /**
   * POST page / funnel analytics (public; JWT optional if user logged in).
   * URL: POST {API_BASE_URL}/api/visits/track-visit
   * Headers: Content-Type: application/json
   * Body (JSON) — built in src/hooks/useTrackVisit.js (CRM / vectors.daiv.co.in shape):
   *   source, page, userId, sessionId, eventType, poojaSlug, amount
   *   eventType: "page_view" | "booking_start" | "payment_success"
   *   amount: number (0 if unknown; package price on book lead; payment amount on thank-you)
   */
  trackVisit: `${API_BASE_URL}/api/visits/track-visit`,
  /** GET feature flags list — `/api/features` (public, no JWT). */
  features: `${API_BASE_URL}/api/features`,
  /**
   * GET one flag on/off — `/api/features/check` (public, no JWT).
   * Send `X-Feature-Key` + `X-Env-Key` headers. Do not put the env secret in the URL.
   */
  featureCheck: `${API_BASE_URL}/api/features/check`,
  /** GET list of feedback cards/testimonials */
  feedback: `${API_BASE_URL}/api/feedback`,
  /** POST feedback form submission */
  feedbackSubmit: `${API_BASE_URL}/api/feedback/submit`,
  /** GET feedback submit status by user + pooja */
  feedbackStatus: (userId, poojaId) =>
    `${API_BASE_URL}/api/feedback/status?${new URLSearchParams({
      userId: String(userId || ''),
      poojaId: String(poojaId || ''),
    }).toString()}`,
  /** GET dynamic feedback form by pooja */
  feedbackForm: (poojaId) =>
    `${API_BASE_URL}/api/feedback/form?${new URLSearchParams({ poojaId: String(poojaId || '') }).toString()}`,
  /** GET divine moments (YouTube Shorts) */
  divineMoments: `${API_BASE_URL}/api/divine-moments`,
  /** GET Daiv Nitya — approved nitya media (videos/images). Public, no auth. */
  nityaMediaApproved: `${API_BASE_URL}/api/nitya-media/approved`,
  /**
   * Nitya media likes — public, no auth.
   * GET  → { data: { nityaId, likeCount } }
   * POST → increments like once per first like
   * Dislike is handled client-side (−1) so re-like does not stack counts.
   */
  nityaMediaLikes: (nityaId) => `${API_BASE_URL}/api/public/nitya-media/${nityaId}/likes`,
  /** POST increment view count for a single nitya media item. Public, no auth. */
  nityaMediaView: (nityaId) => `${API_BASE_URL}/api/nitya-media/${nityaId}/view`,
  /** GET gotras, nakshatras, rashis for person details */
  gotras: `${API_BASE_URL}/api/gotras/users`,
  nakshatras: `${API_BASE_URL}/api/nakshatras/users`,
  rashis: `${API_BASE_URL}/api/rashis/users`,
  /** Favorite poojas – GET list, POST add. Requires JWT */
  favoritePoojas: `${API_BASE_URL}/api/favorite-poojas`,
  /** PATCH remove from favorites */
  favoritePoojasUnfavorite: (poojaId) => `${API_BASE_URL}/api/favorite-poojas/${poojaId}/unfavorite`,
  /** GET kshetras (temples) — optional `?lang=en|hi|kn|te` */
  kshetras: `${API_BASE_URL}/api/kshetras`,
  /** GET deities (no auth) */
  deities: `${API_BASE_URL}/api/deities`,
  /** GET daily horoscope: ?date=dd/MM/yyyy&zodiac=ARIES&latitude=..&longitude=..&languageCode=en_US */
  dailyHoroscope: `${API_BASE_URL}/api/astro/daily-horoscope`,
  /** GET panchang: ?date=dd/MM/yyyy&latitude=..&longitude=..&languageCode=en_US */
  panchang: `${API_BASE_URL}/api/astro/panchang`,
  /** GET muhurat by activity: ?date=dd/MM/yyyy&latitude=..&longitude=..&languageCode=en_US */
  muhuratByActivity: `${API_BASE_URL}/api/astro/muhurat-by-activity`,
  /** POST subscribe to WhatsApp Panchang: /api/whatsapp-subscribers */
  whatsappSubscribe: `${API_BASE_URL}/api/whatsapp-subscribers`,
  /** GET blog categories (no auth) */
  blogCategories: `${API_BASE_URL}/api/blog-categories`,
  /** GET pooja categories (no auth) */
  poojaCategories: `${API_BASE_URL}/api/pooja-categories`,
  /** GET tithis for sidebar filter (no auth) */
  tithis: `${API_BASE_URL}/api/tithis`,
  /** GET single tithi by ID (no auth) */
  tithiById: (id) => `${API_BASE_URL}/api/tithis/${encodeURIComponent(String(id || ''))}`,
  /** GET days of grace for sidebar filter (no auth) */
  daysOfGrace: `${API_BASE_URL}/api/days-of-grace`,
  /** GET gallery images — optional `?lang=en|hi|kn|te` for localized description */
  gallery: `${API_BASE_URL}/api/gallery`,
  /** GET gallery category tabs — optional `?lang=` for localized names */
  galleryCategories: `${API_BASE_URL}/api/gallery-categories`,
  /** GET store products for devotees — optional `?lang=` */
  productsForUsers: (lang = 'en') =>
    `${API_BASE_URL}/api/products/getall/users?lang=${encodeURIComponent(lang || 'en')}`,
  /** POST initiate product order (JWT optional for guests) */
  productOrdersInitiate: `${API_BASE_URL}/api/product-orders/initiate`,
  /** POST shipping quote for saved address (JWT required; guests use initiate fallback) */
  productOrdersShippingQuote: `${API_BASE_URL}/api/product-orders/shipping-quote`,
  /** POST confirm product order payment (JWT optional for guests) */
  productOrdersConfirm: `${API_BASE_URL}/api/product-orders/confirm`,
  /** GET product order by id (JWT required for full details; guests may use order id from thank-you link) */
  productOrderById: (orderId) =>
    `${API_BASE_URL}/api/product-orders/${encodeURIComponent(String(orderId || ''))}`,
  /** GET logged-in devotee product orders (full history) */
  productOrdersMe: `${API_BASE_URL}/api/product-orders/me`,
  /** GET in-progress product orders (paid, not yet shipped) — JWT required */
  productOrdersMeTracking: `${API_BASE_URL}/api/product-orders/me/tracking`,
  /** GET courier / AWB shipment tracking for an order — JWT required */
  productOrderTrackingById: (orderId) =>
    `${API_BASE_URL}/api/product-orders/${encodeURIComponent(String(orderId || ''))}/tracking`,
  /** Cart – requires JWT. POST add, GET list, DELETE item */
  cart: `${API_BASE_URL}/api/cart`,
  cartDelete: (cartItemId) => `${API_BASE_URL}/api/cart/${cartItemId}`,
  /** Store product cart – GET one / DELETE by productId */
  cartProduct: (productId) =>
    `${API_BASE_URL}/api/cart/products/${encodeURIComponent(String(productId || ''))}`,
  /** User profile – GET by id, PATCH to update. Override with REACT_APP_USER_API_BASE_URL if needed */
  userById: (userId) => `${process.env.REACT_APP_USER_API_BASE_URL || API_BASE_URL}/api/users/${userId}`,
  userUpdate: (userId) => `${process.env.REACT_APP_USER_API_BASE_URL || API_BASE_URL}/api/users/${userId}`,
  /** Saved delivery addresses – JWT required */
  userAddresses: `${API_BASE_URL}/api/users/me/addresses`,
  userAddressById: (addressId) => `${API_BASE_URL}/api/users/me/addresses/${addressId}`,
  userAddressSetDefault: (addressId) => `${API_BASE_URL}/api/users/me/addresses/${addressId}/default`,
  /** GET pincodes for a district */
  locationsDistrictPincodes: (districtId) =>
    `${API_BASE_URL}/api/locations/districts/${encodeURIComponent(String(districtId || ''))}/pincodes?size=500`,
  /** Location lookups for address form */
  locationsStates: `${API_BASE_URL}/api/locations/states`,
  locationsDistricts: (stateId) =>
    `${API_BASE_URL}/api/locations/states/${encodeURIComponent(String(stateId || ''))}/districts?size=500`,
  /** GET acharya profile for personalized sankalpa matching */
  acharyaProfile: (acharyaId, lang = 'en') =>
    `${API_BASE_URL}/api/acharyas/${encodeURIComponent(String(acharyaId || ''))}/profile?lang=${encodeURIComponent(String(lang || 'en'))}`,
};

/** GET list of user's bookings: /api/poojabookings?userId=xxx */
export const getBookingsListEndpoint = () => API_ENDPOINTS.poojaBookingsByUser;

/** GET single booking: /api/poojabookings/{bookingId}?userId=xxx */
export const getBookingDetailEndpoint = (bookingId) =>
  API_ENDPOINTS.poojaBookingById(bookingId);

/**
 * Maps backend `galleryCategoryId` (UUID) to filter slug: rituals | kshetras | prasadam | live.
 * Items with unknown IDs still appear under "All". Add entries when your CRM defines category UUIDs.
 */
export const GALLERY_CATEGORY_ID_TO_FILTER = {
  '5aa393ae-ef06-49d7-a285-de7aa154b6d9': 'rituals',
};
